import React, { useState, useEffect, useContext, useRef, useMemo } from 'react';
import { AppContext } from '@/App';
import axios from 'axios';
import {
  FileText, Upload, X, AlertCircle, RefreshCw,
  Folder, Ship, Calendar, ChevronDown, ChevronUp,
  Trash2, Eye, CheckCircle, FileWarning, Plus, Split,
  Package, Edit2, Save
} from 'lucide-react';

// Document types required for each consignment (12 documents)
const DOCUMENT_TYPES = [
  { id: 'bl_copy', name: 'B/L Copy', required: true },
  { id: 'packing_list', name: 'Packing List', required: true },
  { id: 'certificate_of_origin', name: 'Certificate of Origin', required: true },
  { id: 'chemical_certificate', name: 'Chemical Certificate', required: true },
  { id: 'form_6', name: 'Form 6', required: true },
  { id: 'form_9', name: 'Form 9', required: true },
  { id: 'form_2', name: 'Form 2', required: false },
  { id: 'psic', name: 'PSIC', required: true },
  { id: 'commercial_invoice', name: 'Commercial Invoice', required: true },
  { id: 'insurance_certificate', name: 'Insurance Certificate', required: true },
  { id: 'fta', name: 'FTA (Free Trade Agreement)', required: false },
  { id: 'section_3', name: 'Section 3 (for FTA)', required: false },
];
const REQUIRED_COUNT = DOCUMENT_TYPES.filter((d) => d.required).length;

const emptyConsignmentForm = {
  consignmentNumber: '',
  quantity: '',
  containerNo: '',
  eta: '',
  status: 'Pending',
  remarks: '',
};

function ImportDocuments() {
  const { API } = useContext(AppContext);
  const [contracts, setContracts] = useState([]);
  const [consignments, setConsignments] = useState([]); // flat list across all contracts
  const [documents, setDocuments] = useState({}); // keyed by consignmentId → {docType: doc}
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({}); // contractId → bool
  const [docModal, setDocModal] = useState(null); // { consignmentId, label, subtitle }
  const [consModal, setConsModal] = useState(null); // { mode: 'create'|'edit', contractId, consignmentId? }
  const [consForm, setConsForm] = useState(emptyConsignmentForm);
  const [uploading, setUploading] = useState({});
  const [message, setMessage] = useState(null);
  const fileInputRefs = useRef({});

  useEffect(() => {
    refreshAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshAll = async () => {
    setLoading(true);
    await Promise.all([fetchContracts(), fetchConsignments(), fetchDocuments()]);
    setLoading(false);
  };

  const fetchContracts = async () => {
    try {
      const r = await axios.get(`${API}/imports/sales-contracts`);
      setContracts(r.data.data || []);
    } catch (e) {
      console.error('contracts', e);
    }
  };

  const fetchConsignments = async () => {
    try {
      const r = await axios.get(`${API}/imports/consignments`);
      setConsignments(r.data.data || []);
    } catch (e) {
      console.error('consignments', e);
    }
  };

  const fetchDocuments = async () => {
    try {
      const r = await axios.get(`${API}/imports/documents`);
      const docs = r.data.data || [];
      const grouped = {};
      docs.forEach((d) => {
        if (!grouped[d.consignmentId]) grouped[d.consignmentId] = {};
        grouped[d.consignmentId][d.documentType] = d;
      });
      setDocuments(grouped);
    } catch (e) {
      console.error('documents', e);
    }
  };

  const flash = (type, text, ms = 3000) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), ms);
  };

  // ---------- Consignment CRUD ----------
  const openCreateConsignment = (contractId) => {
    setConsForm(emptyConsignmentForm);
    setConsModal({ mode: 'create', contractId });
  };
  const openEditConsignment = (c) => {
    setConsForm({
      consignmentNumber: c.consignmentNumber || '',
      quantity: c.quantity ?? '',
      containerNo: c.containerNo || '',
      eta: c.eta || '',
      status: c.status || 'Pending',
      remarks: c.remarks || '',
    });
    setConsModal({ mode: 'edit', contractId: c.contractId, consignmentId: c.id });
  };

  const saveConsignment = async (e) => {
    e.preventDefault();
    if (!consForm.consignmentNumber.trim()) {
      flash('error', 'Consignment number is required');
      return;
    }
    try {
      if (consModal.mode === 'create') {
        await axios.post(`${API}/imports/consignments`, {
          ...consForm,
          contractId: consModal.contractId,
        });
        flash('success', 'Consignment created');
      } else {
        await axios.put(
          `${API}/imports/consignments/${consModal.consignmentId}`,
          consForm
        );
        flash('success', 'Consignment updated');
      }
      setConsModal(null);
      fetchConsignments();
    } catch (err) {
      flash('error', err.response?.data?.detail || 'Failed to save consignment');
    }
  };

  const deleteConsignment = async (c) => {
    const docCount = Object.keys(documents[c.id] || {}).length;
    const msg = docCount
      ? `Delete ${c.consignmentNumber}? ${docCount} document(s) will also be deleted.`
      : `Delete ${c.consignmentNumber}?`;
    if (!window.confirm(msg)) return;
    try {
      await axios.delete(
        `${API}/imports/consignments/${c.id}${docCount ? '?force=true' : ''}`
      );
      flash('success', 'Consignment deleted');
      fetchConsignments();
      fetchDocuments();
    } catch (err) {
      flash('error', err.response?.data?.detail || 'Failed to delete consignment');
    }
  };

  // ---------- Documents ----------
  const uploadDoc = async (consignmentId, documentType, file) => {
    if (!file) return;
    const key = `${consignmentId}_${documentType}`;
    setUploading((p) => ({ ...p, [key]: true }));
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('consignmentId', consignmentId);
      fd.append('documentType', documentType);
      await axios.post(`${API}/imports/documents/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      flash(
        'success',
        `${DOCUMENT_TYPES.find((d) => d.id === documentType)?.name} uploaded`
      );
      fetchDocuments();
    } catch (err) {
      flash('error', err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading((p) => ({ ...p, [key]: false }));
    }
  };

  const deleteDoc = async (consignmentId, documentType) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await axios.delete(
        `${API}/imports/documents/${consignmentId}/${documentType}`
      );
      flash('success', 'Document deleted');
      fetchDocuments();
    } catch {
      flash('error', 'Failed to delete document');
    }
  };

  // ---------- Derived ----------
  // Group consignments by contractId
  const consByContract = useMemo(() => {
    const m = {};
    consignments.forEach((c) => {
      if (!m[c.contractId]) m[c.contractId] = [];
      m[c.contractId].push(c);
    });
    // Sort each group by consignmentNumber for stable display
    Object.values(m).forEach((arr) =>
      arr.sort((a, b) =>
        (a.consignmentNumber || '').localeCompare(b.consignmentNumber || '')
      )
    );
    return m;
  }, [consignments]);

  const contractTotalQty = (contract) =>
    (contract.items || []).reduce(
      (s, i) => s + (parseFloat(i.quantity || 0) || 0),
      0
    );
  const allocatedQty = (contractId) =>
    (consByContract[contractId] || []).reduce(
      (s, c) => s + (parseFloat(c.quantity || 0) || 0),
      0
    );

  const legacyDocsForContract = (contractId) => {
    // Docs uploaded BEFORE the split feature stored contractId as consignmentId.
    // Surface them as a separate read-only "Contract-level" row for backward-compat.
    if (documents[contractId] && Object.keys(documents[contractId]).length) {
      return documents[contractId];
    }
    return null;
  };

  const consignmentProgress = (consignmentId) => {
    const docs = documents[consignmentId] || {};
    const uploadedRequired = DOCUMENT_TYPES.filter(
      (d) => d.required && docs[d.id]
    ).length;
    return {
      uploadedRequired,
      requiredTotal: REQUIRED_COUNT,
      percentage: Math.round((uploadedRequired / REQUIRED_COUNT) * 100),
      complete: uploadedRequired === REQUIRED_COUNT,
    };
  };

  const totalContracts = contracts.length;
  const totalConsignments = consignments.length;
  const completeConsignments = consignments.filter(
    (c) => consignmentProgress(c.id).complete
  ).length;

  const fmtDate = (s) => {
    if (!s) return '-';
    try {
      return new Date(s).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
    } catch {
      return s;
    }
  };

  return (
    <div className="space-y-6" data-testid="import-documents-page">
      {message && (
        <div
          className={`p-4 rounded-xl flex items-center gap-3 ${
            message.type === 'success'
              ? 'bg-green-50 border border-green-200 text-green-700'
              : 'bg-red-50 border border-red-200 text-red-700'
          }`}
        >
          {message.type === 'success' ? (
            <CheckCircle size={20} />
          ) : (
            <AlertCircle size={20} />
          )}
          {message.text}
        </div>
      )}

      {/* Header */}
      <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl">
              <Folder className="text-white" size={28} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Import Documents</h1>
              <p className="text-sm text-gray-500">
                Split Sales Contracts into consignments and manage each one's documents
              </p>
            </div>
          </div>
          <button
            onClick={refreshAll}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-50 text-indigo-600 rounded-lg hover:bg-indigo-100 transition"
            data-testid="import-docs-refresh-btn"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>

        <div className="grid grid-cols-3 gap-4 mt-6">
          <div className="bg-gray-50 rounded-xl p-4 text-center">
            <p className="text-3xl font-bold text-gray-900">{totalContracts}</p>
            <p className="text-sm text-gray-500">Sales Contracts</p>
          </div>
          <div className="bg-indigo-50 rounded-xl p-4 text-center">
            <p className="text-3xl font-bold text-indigo-600">
              {totalConsignments}
            </p>
            <p className="text-sm text-gray-500">Total Consignments</p>
          </div>
          <div className="bg-green-50 rounded-xl p-4 text-center">
            <p className="text-3xl font-bold text-green-600">
              {completeConsignments}
            </p>
            <p className="text-sm text-gray-500">Documents Complete</p>
          </div>
        </div>
      </div>

      {/* Contracts List */}
      {loading ? (
        <div className="bg-white rounded-xl p-8 border border-gray-200 text-center">
          <RefreshCw
            className="animate-spin mx-auto mb-3 text-indigo-500"
            size={32}
          />
          <p className="text-gray-500">Loading…</p>
        </div>
      ) : contracts.length === 0 ? (
        <div className="bg-white rounded-xl p-8 border border-gray-200 text-center">
          <Ship className="mx-auto mb-3 text-gray-400" size={48} />
          <p className="text-gray-500 font-medium">No sales contracts found</p>
          <p className="text-sm text-gray-400 mt-1">
            Create a Sales Contract in Imports to get started
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {contracts.map((contract) => {
            const isOpen = !!expanded[contract.id];
            const cList = consByContract[contract.id] || [];
            const total = contractTotalQty(contract);
            const alloc = allocatedQty(contract.id);
            const remaining = total - alloc;
            const over = alloc > total + 0.001;
            const legacyDocs = legacyDocsForContract(contract.id);

            return (
              <div
                key={contract.id}
                className="bg-white rounded-xl border border-gray-200 shadow-sm"
                data-testid={`contract-card-${contract.id}`}
              >
                {/* Contract header */}
                <div
                  className="p-5 flex items-center gap-4 cursor-pointer hover:bg-gray-50 rounded-t-xl"
                  onClick={() =>
                    setExpanded((p) => ({ ...p, [contract.id]: !p[contract.id] }))
                  }
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="px-2.5 py-1 bg-indigo-100 text-indigo-700 rounded-md text-xs font-bold">
                        {contract.contractNumber || contract.id?.slice(0, 12)}
                      </span>
                      <span className="text-sm text-gray-600">
                        {contract.supplierName || '—'}
                      </span>
                      <span className="text-xs text-gray-400 hidden sm:inline">
                        · {contract.supplierCountry || 'International'} ·{' '}
                        {fmtDate(contract.contractDate)}
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-3 text-sm">
                      <span>
                        <span className="text-gray-500">Contract qty:</span>{' '}
                        <b>{total.toFixed(2)} MT</b>
                      </span>
                      <span>
                        <span className="text-gray-500">Allocated:</span>{' '}
                        <b className={over ? 'text-red-600' : 'text-gray-900'}>
                          {alloc.toFixed(2)} MT
                        </b>
                      </span>
                      <span>
                        <span className="text-gray-500">Remaining:</span>{' '}
                        <b className={remaining < 0 ? 'text-red-600' : 'text-green-600'}>
                          {remaining.toFixed(2)} MT
                        </b>
                      </span>
                      <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs">
                        {cList.length} consignment{cList.length === 1 ? '' : 's'}
                      </span>
                      {over && (
                        <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs font-semibold flex items-center gap-1">
                          <AlertCircle size={12} /> Over-allocated
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openCreateConsignment(contract.id);
                      }}
                      className="flex items-center gap-1.5 px-3 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700"
                      data-testid={`add-consignment-btn-${contract.id}`}
                    >
                      <Split size={14} /> Add Consignment
                    </button>
                    {isOpen ? (
                      <ChevronUp size={20} className="text-gray-400" />
                    ) : (
                      <ChevronDown size={20} className="text-gray-400" />
                    )}
                  </div>
                </div>

                {/* Expanded consignments list */}
                {isOpen && (
                  <div className="border-t border-gray-200 bg-gray-50/50 p-4 space-y-3">
                    {cList.length === 0 && !legacyDocs && (
                      <div className="text-center py-6 bg-white rounded-lg border border-dashed border-gray-300">
                        <Package
                          size={28}
                          className="mx-auto text-gray-400 mb-2"
                        />
                        <p className="text-sm text-gray-500">
                          No consignments yet. Click{' '}
                          <b>Add Consignment</b> above to split this contract.
                        </p>
                      </div>
                    )}

                    {cList.map((c) => {
                      const prog = consignmentProgress(c.id);
                      return (
                        <div
                          key={c.id}
                          className={`bg-white rounded-lg p-4 border-l-4 ${
                            prog.complete
                              ? 'border-l-green-500'
                              : 'border-l-amber-500'
                          } border border-gray-200`}
                          data-testid={`consignment-row-${c.id}`}
                        >
                          <div className="flex flex-col lg:flex-row lg:items-center gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="px-2.5 py-1 bg-amber-100 text-amber-800 rounded text-xs font-bold">
                                  {c.consignmentNumber}
                                </span>
                                <span className="text-sm font-semibold text-gray-900">
                                  {parseFloat(c.quantity || 0).toFixed(2)}{' '}
                                  {c.unit || 'MT'}
                                </span>
                                {c.containerNo && (
                                  <span className="text-xs text-gray-600 bg-gray-100 px-2 py-0.5 rounded">
                                    Container: {c.containerNo}
                                  </span>
                                )}
                                <span
                                  className={`text-xs px-2 py-0.5 rounded ${
                                    c.status === 'Arrived'
                                      ? 'bg-green-100 text-green-700'
                                      : c.status === 'In Transit'
                                      ? 'bg-blue-100 text-blue-700'
                                      : 'bg-gray-100 text-gray-700'
                                  }`}
                                >
                                  {c.status || 'Pending'}
                                </span>
                                {c.eta && (
                                  <span className="text-xs text-gray-500 flex items-center gap-1">
                                    <Calendar size={12} /> ETA{' '}
                                    {fmtDate(c.eta)}
                                  </span>
                                )}
                              </div>
                              {c.remarks && (
                                <p className="text-xs text-gray-500 mt-1">
                                  {c.remarks}
                                </p>
                              )}

                              <div className="flex items-center gap-3 mt-3">
                                <div className="flex-1 bg-gray-200 rounded-full h-2 max-w-xs">
                                  <div
                                    className={`h-2 rounded-full ${
                                      prog.complete
                                        ? 'bg-green-500'
                                        : prog.percentage >= 50
                                        ? 'bg-amber-500'
                                        : 'bg-red-500'
                                    }`}
                                    style={{ width: `${prog.percentage}%` }}
                                  />
                                </div>
                                <span className="text-xs font-medium text-gray-600 whitespace-nowrap">
                                  {prog.uploadedRequired}/{prog.requiredTotal} required
                                </span>
                              </div>
                            </div>

                            <div className="flex items-center gap-2">
                              <button
                                onClick={() =>
                                  setDocModal({
                                    consignmentId: c.id,
                                    label: c.consignmentNumber,
                                    subtitle: `${c.supplierName} · ${parseFloat(
                                      c.quantity || 0
                                    ).toFixed(2)} ${c.unit || 'MT'}`,
                                  })
                                }
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-50 text-indigo-600 rounded-lg hover:bg-indigo-100 text-sm font-medium"
                                data-testid={`open-docs-${c.id}`}
                              >
                                <Upload size={14} /> Documents
                              </button>
                              <button
                                onClick={() => openEditConsignment(c)}
                                className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                                title="Edit consignment"
                                data-testid={`edit-consignment-${c.id}`}
                              >
                                <Edit2 size={14} />
                              </button>
                              <button
                                onClick={() => deleteConsignment(c)}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                                title="Delete consignment"
                                data-testid={`delete-consignment-${c.id}`}
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}

                    {/* Legacy contract-level documents */}
                    {legacyDocs && (
                      <div
                        className="bg-white rounded-lg p-4 border border-dashed border-gray-300"
                        data-testid={`legacy-docs-${contract.id}`}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div>
                            <p className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                              <FileText size={14} /> Contract-level documents
                              <span className="text-xs font-normal text-gray-500">
                                (uploaded before the split feature —{' '}
                                {Object.keys(legacyDocs).length} file
                                {Object.keys(legacyDocs).length === 1 ? '' : 's'})
                              </span>
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                              Open the document panel to view or move them onto a
                              specific consignment.
                            </p>
                          </div>
                          <button
                            onClick={() =>
                              setDocModal({
                                consignmentId: contract.id,
                                label: `${contract.contractNumber} (Contract-level)`,
                                subtitle: contract.supplierName,
                              })
                            }
                            className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200"
                          >
                            <Eye size={14} className="inline mr-1" /> View
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Consignment Create/Edit Modal */}
      {consModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          data-testid="consignment-modal"
        >
          <form
            onSubmit={saveConsignment}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-lg"
          >
            <div className="flex items-center justify-between p-5 border-b border-gray-200 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-t-2xl">
              <h2 className="text-lg font-bold text-white">
                {consModal.mode === 'create'
                  ? 'Add Consignment'
                  : 'Edit Consignment'}
              </h2>
              <button
                type="button"
                onClick={() => setConsModal(null)}
                className="p-2 text-white/80 hover:bg-white/10 rounded-lg"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700">
                  Consignment Number *
                </label>
                <input
                  type="text"
                  value={consForm.consignmentNumber}
                  onChange={(e) =>
                    setConsForm((p) => ({
                      ...p,
                      consignmentNumber: e.target.value,
                    }))
                  }
                  placeholder="e.g. CON-01, Shipment-A, BL-12345/1"
                  className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  required
                  data-testid="consignment-number-input"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Must be unique within this contract.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-gray-700">
                    Quantity (MT) *
                  </label>
                  <input
                    type="number"
                    step="0.001"
                    value={consForm.quantity}
                    onChange={(e) =>
                      setConsForm((p) => ({ ...p, quantity: e.target.value }))
                    }
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg"
                    required
                    data-testid="consignment-qty-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">
                    Status
                  </label>
                  <select
                    value={consForm.status}
                    onChange={(e) =>
                      setConsForm((p) => ({ ...p, status: e.target.value }))
                    }
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option>Pending</option>
                    <option>In Transit</option>
                    <option>Arrived</option>
                    <option>Cleared</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-gray-700">
                    Container No
                  </label>
                  <input
                    type="text"
                    value={consForm.containerNo}
                    onChange={(e) =>
                      setConsForm((p) => ({ ...p, containerNo: e.target.value }))
                    }
                    placeholder="e.g. ABCU1234567"
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">
                    ETA
                  </label>
                  <input
                    type="date"
                    value={consForm.eta}
                    onChange={(e) =>
                      setConsForm((p) => ({ ...p, eta: e.target.value }))
                    }
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">
                  Remarks
                </label>
                <textarea
                  value={consForm.remarks}
                  onChange={(e) =>
                    setConsForm((p) => ({ ...p, remarks: e.target.value }))
                  }
                  rows="2"
                  className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
            </div>
            <div className="p-4 border-t border-gray-200 flex justify-end gap-2 bg-gray-50 rounded-b-2xl">
              <button
                type="button"
                onClick={() => setConsModal(null)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium"
                data-testid="consignment-save-btn"
              >
                <Save size={16} />
                {consModal.mode === 'create' ? 'Create' : 'Save'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Document upload modal (for a consignment) */}
      {docModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          data-testid="documents-modal"
        >
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-5 border-b border-gray-200 bg-gradient-to-r from-indigo-500 to-purple-600">
              <div>
                <span className="px-2 py-0.5 bg-white/20 text-white rounded text-xs font-bold">
                  {docModal.label}
                </span>
                <h2 className="text-lg font-bold text-white mt-1">Documents</h2>
                <p className="text-white/80 text-sm">{docModal.subtitle}</p>
              </div>
              <button
                onClick={() => setDocModal(null)}
                className="p-2 text-white/80 hover:bg-white/10 rounded-lg"
                data-testid="documents-modal-close"
              >
                <X size={22} />
              </button>
            </div>

            <div className="p-5 overflow-y-auto">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {DOCUMENT_TYPES.map((docType) => {
                  const uploaded =
                    (documents[docModal.consignmentId] || {})[docType.id];
                  const key = `${docModal.consignmentId}_${docType.id}`;
                  const busy = uploading[key];
                  return (
                    <div
                      key={docType.id}
                      className={`p-4 rounded-xl border-2 ${
                        uploaded
                          ? 'bg-green-50 border-green-300'
                          : docType.required
                          ? 'bg-red-50 border-red-200 border-dashed'
                          : 'bg-gray-50 border-gray-200 border-dashed'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <p
                            className={`font-semibold text-sm ${
                              uploaded
                                ? 'text-green-700'
                                : docType.required
                                ? 'text-red-700'
                                : 'text-gray-700'
                            }`}
                          >
                            {docType.name}
                            {docType.required && !uploaded && (
                              <span className="text-red-500 ml-1">*</span>
                            )}
                          </p>
                          {uploaded && (
                            <p
                              className="text-xs text-gray-500 mt-1 truncate"
                              title={uploaded.fileName}
                            >
                              {uploaded.fileName}
                            </p>
                          )}
                        </div>
                        {uploaded ? (
                          <CheckCircle
                            size={18}
                            className="text-green-500 flex-shrink-0"
                          />
                        ) : docType.required ? (
                          <FileWarning
                            size={18}
                            className="text-red-400 flex-shrink-0"
                          />
                        ) : (
                          <FileText
                            size={18}
                            className="text-gray-400 flex-shrink-0"
                          />
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-3">
                        {uploaded ? (
                          <>
                            <a
                              href={`${API}/imports/documents/file/${uploaded.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex-1 flex items-center justify-center gap-1 px-3 py-2 text-xs bg-white text-gray-700 rounded-lg border border-gray-300 hover:bg-gray-50 font-medium"
                            >
                              <Eye size={14} /> View
                            </a>
                            <button
                              onClick={() =>
                                deleteDoc(docModal.consignmentId, docType.id)
                              }
                              className="px-3 py-2 text-xs bg-white text-red-600 rounded-lg border border-red-300 hover:bg-red-50"
                            >
                              <Trash2 size={14} />
                            </button>
                          </>
                        ) : (
                          <>
                            <input
                              type="file"
                              ref={(el) =>
                                (fileInputRefs.current[key] = el)
                              }
                              onChange={(e) =>
                                uploadDoc(
                                  docModal.consignmentId,
                                  docType.id,
                                  e.target.files[0]
                                )
                              }
                              className="hidden"
                              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                            />
                            <button
                              onClick={() =>
                                fileInputRefs.current[key]?.click()
                              }
                              disabled={busy}
                              className={`w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-medium text-sm ${
                                docType.required
                                  ? 'bg-red-600 text-white hover:bg-red-700'
                                  : 'bg-gray-600 text-white hover:bg-gray-700'
                              } disabled:opacity-50`}
                              data-testid={`upload-${docType.id}`}
                            >
                              {busy ? (
                                <>
                                  <RefreshCw
                                    size={14}
                                    className="animate-spin"
                                  />{' '}
                                  Uploading…
                                </>
                              ) : (
                                <>
                                  <Upload size={14} /> Upload
                                </>
                              )}
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-5 pt-4 border-t border-gray-200 text-xs text-gray-500 flex flex-wrap items-center gap-4">
                <span className="flex items-center gap-1">
                  <span className="w-3 h-3 bg-red-200 rounded" /> Required
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-3 h-3 bg-gray-200 rounded" /> Optional
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-3 h-3 bg-green-200 rounded" /> Uploaded
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ImportDocuments;
